import { DecoratedFormProps, InjectedFormProps, reduxForm } from 'redux-form';
import { FormError } from 'src/components/redux-form/FormError';
import React from 'react';
import { maxLength, minValue, required, Validators } from 'src/components/redux-form/utils/validators';
import classNames from 'classnames';
import { FieldInput } from 'src/components/redux-form/fields/FieldInput';
import { Link } from 'react-router-dom';
import Router from 'src/navigation/router';
import { routes } from 'src/navigation';
import styles from './assets/adminWork-form.module.scss';
import { faSave, faBan } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { asyncPersistAdminWork } from 'src/store/actions/adminWork-actions';
import { Dispatch } from 'redux';
import { toast } from 'react-toastify';
import { IProvider } from '../../../../../types/provider';
import { FieldDate } from '../../../../../components/redux-form/fields/FieldDate';
import { FieldDropdown } from '../../../../../components/redux-form/fields/FieldDropdown';

export interface AdminWorkFormData {
    id: string | number;
    adminWorkDate: string;
    month: any;
    provider: any;
    startTime: any;
    endTime: any;
    expenseDate: string;
    amount: number;
    description: string;
    note: string;
    providerDropDown: SelectObject | undefined;
}

interface AdminWorkFormProps {
    providers: Partial<IProvider>[];
}

type InjectedProps = InjectedFormProps<AdminWorkFormData, AdminWorkFormProps>;

const validators: Validators<AdminWorkFormData> = {
    adminWorkDate: [required({ message: 'Admin work Date is required.' }), maxLength({ max: 255 })],
    amount: [required({ message: 'Amount is required.' }), minValue({ min: 1 }), maxLength({ max: 255 })],
    providerDropDown: [required({ message: 'Provider is required.' }), maxLength({ max: 255 })],
    description: [required({ message: 'Description is required.' }), maxLength({ max: 255 })],
    note: [required({ message: 'Note is required.' }), maxLength({ max: 255 })],
};

const AdminWorkForm: React.FunctionComponent<InjectedProps & AdminWorkFormProps> = ({
    handleSubmit,
    error,
    dirty,
    submitting,
    initialValues,
    providers,
}: InjectedProps & AdminWorkFormProps) => {
    const isNew = !initialValues || !initialValues.id;
    const providerValues: SelectObject[] = [];
    providers.forEach((prov: Partial<IProvider>) => {
        providerValues.push({ value: prov.id, label: `${prov.fullName}` });
    });
    providerValues.sort((a: SelectObject, b: SelectObject) => {
        const textA = a.label.toUpperCase();
        const textB = b.label.toUpperCase();
        return textA < textB ? -1 : textA > textB ? 1 : 0;
    });
    const today = new Date();
    return (
        <form onSubmit={handleSubmit} className={classNames(styles.form)}>
            <FormError error={error} />
            <FieldInput
                name="id"
                type="hidden"
                wrapperConfig={{
                    classes: {
                        inputWrapper: 'm-0',
                    },
                }}
            />
            <div className="d-flex flex-row">
                <div className="w-50 mr-5">
                    <FieldDate
                        name="adminWorkDate"
                        validate={validators.adminWorkDate}
                        type="text"
                        date={true}
                        disabled={!isNew}
                        wrapperConfig={{
                            label: 'Admin Work Date: *',
                        }}
                    />
                </div>

                <div className="w-50">
                    <FieldDate
                        name="startTime"
                        validate={validators.startTime}
                        min={new Date(today.getFullYear(), today.getMonth(), today.getDate(), 7)}
                        max={new Date(today.getFullYear(), today.getMonth(), today.getDate(), 21)}
                        type="text"
                        formatType={'LT'}
                        date={false}
                        time={true}
                        wrapperConfig={{
                            label: 'Start Time: ',
                        }}
                    />
                </div>
            </div>
            <div className="d-flex flex-row">
                <div className="w-50 mr-5">
                    <FieldInput
                        name="description"
                        type="text"
                        validate={validators.description}
                        wrapperConfig={{
                            label: 'Description *',
                        }}
                    />
                </div>
                <div className="w-50">
                    <FieldDate
                        name="endTime"
                        validate={validators.endTime}
                        type="text"
                        min={new Date(today.getFullYear(), today.getMonth(), today.getDate(), 7)}
                        max={new Date(today.getFullYear(), today.getMonth(), today.getDate(), 21)}
                        formatType={'LT'}
                        date={false}
                        time={true}
                        wrapperConfig={{
                            label: 'End Time: ',
                        }}
                    />
                </div>
            </div>
            <FieldDropdown
                name="providerDropDown"
                data={providerValues}
                textField={'label'}
                valueField={'value'}
                disabled={!isNew}
                validate={validators.providerDropDown}
                wrapperConfig={{
                    label: 'Provider: *',
                }}
            />

            <div className={classNames(styles.actions)}>
                <button
                    type="submit"
                    className={classNames('btn', 'btn-success', 'mr-3')}
                    disabled={!dirty || submitting}
                >
                    <FontAwesomeIcon icon={faSave} /> {isNew ? 'Create' : 'Update'}
                </button>
                <Link className={classNames('btn', 'btn-danger')} to={Router.generate(routes.INTERNALS_LIST)}>
                    <FontAwesomeIcon icon={faBan} /> Cancel
                </Link>
            </div>
        </form>
    );
};

export default reduxForm<AdminWorkFormData, AdminWorkFormProps>({
    form: 'adminWork-form',
    onSubmit: async (data: AdminWorkFormData, dispatch: Dispatch<any>, props: AdminWorkFormProps) => {
        const endTimeT = new Date(data.endTime);
        let endTime = endTimeT.getHours();
        if (!endTime || endTime < 6) {
            endTime = 24;
        }
        endTime = +endTime * 60 + endTimeT.getUTCMinutes();

        const startT = new Date(data.startTime);
        let start = startT.getHours();
        start = 60 * +start + startT.getUTCMinutes();

        if (endTime <= start) {
            toast.error('Incorrect time!');
        } else {
            await asyncPersistAdminWork(data, dispatch);
        }
    },
    onSubmitSuccess(
        result: any,
        dispatch: Dispatch<any>,
        props: DecoratedFormProps<AdminWorkFormData, AdminWorkFormProps, string>,
    ) {
        //const isNew = !props.initialValues || !props.initialValues.id;
        //toast.success(`AdminWork was successfully ${isNew ? 'created' : 'updated'}.`);
    },

    onSubmitFail(errors: { _error: string }) {
        console.log('errors', errors);
        if (errors?._error) {
            toast.error(errors._error);
        }
    },
})(AdminWorkForm);
